import React from "react";

/**
 * Campusco Logo — institutional wordmark inspired by BlackRock / Vanguard / IDC.
 * A filled monogram square (the "C" mark) sits alongside a bold CAMPUSCO wordmark
 * with a property/management subline.
 * Variants: "primary" | "dark" | "light" | "mono" | "icon"
 */

const PALETTES = {
  primary: { markBg: "#1A3636", markFg: "#FAFAF9", accent: "#D97757", text: "#0F172A", sub: "#475569" },
  dark:    { markBg: "#0F172A", markFg: "#FAFAF9", accent: "#0F172A", text: "#0F172A", sub: "#475569" },
  light:   { markBg: "#FAFAF9", markFg: "#1A3636", accent: "#FAFAF9", text: "#FAFAF9", sub: "#E2E8F0" },
  mono:    { markBg: "#0F172A", markFg: "#FAFAF9", accent: "#0F172A", text: "#0F172A", sub: "#0F172A" },
  icon:    { markBg: "#1A3636", markFg: "#FAFAF9", accent: "#D97757", text: "#0F172A", sub: "#475569" },
};

export const Logo = ({ variant = "primary", className = "" }) => {
  const p = PALETTES[variant] || PALETTES.primary;

  // Icon-only: rounded square with bold serif "C" + accent dot
  if (variant === "icon") {
    return (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className={className}
        role="img" aria-label="Campusco icon">
        <rect x="0" y="0" width="80" height="80" rx="10" fill={p.markBg} />
        <text x="40" y="56" fontFamily="'Playfair Display', Georgia, serif" fontWeight="700"
          fontSize="52" textAnchor="middle" fill={p.markFg}>C</text>
        <circle cx="62" cy="18" r="6" fill={p.accent} />
      </svg>
    );
  }

  // Full wordmark: 48px square monogram + CAMPUSCO + subline
  return (
    <svg viewBox="0 0 380 72" xmlns="http://www.w3.org/2000/svg" className={className}
      role="img" aria-label="Campusco Property Management">
      {/* Monogram box */}
      <g>
        <rect x="0" y="4" width="64" height="64" rx="6" fill={p.markBg} />
        <text
          x="32" y="49"
          fontFamily="'Playfair Display', Georgia, serif"
          fontWeight="700" fontSize="44" textAnchor="middle"
          fill={p.markFg}
        >C</text>
        <circle cx="52" cy="14" r="4.5" fill={p.accent} />
      </g>

      {/* Wordmark */}
      <text
        x="80" y="40"
        fontFamily="'Manrope', -apple-system, sans-serif"
        fontWeight="800"
        fontSize="28"
        letterSpacing="1.5"
        fill={p.text}
      >
        CAMPUSCO
      </text>

      {/* Accent divider */}
      <rect x="80" y="48" width="18" height="2" fill={p.accent} />

      {/* Sub-line */}
      <text
        x="104" y="58"
        fontFamily="'Manrope', -apple-system, sans-serif"
        fontWeight="600"
        fontSize="9"
        letterSpacing="3"
        fill={p.sub}
      >
        PROPERTY MANAGEMENT
      </text>
    </svg>
  );
};

/** Return a standalone SVG string suitable for download. */
export const getLogoSVG = (variant = "primary") => {
  const p = PALETTES[variant] || PALETTES.primary;

  if (variant === "icon") {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="512" height="512">
  <rect x="0" y="0" width="80" height="80" rx="10" fill="${p.markBg}"/>
  <text x="40" y="56" font-family="'Playfair Display', Georgia, serif" font-weight="700"
    font-size="52" text-anchor="middle" fill="${p.markFg}">C</text>
  <circle cx="62" cy="18" r="6" fill="${p.accent}"/>
</svg>`;
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 72" width="1520" height="288">
  <rect x="0" y="4" width="64" height="64" rx="6" fill="${p.markBg}"/>
  <text x="32" y="49" font-family="'Playfair Display', Georgia, serif" font-weight="700"
    font-size="44" text-anchor="middle" fill="${p.markFg}">C</text>
  <circle cx="52" cy="14" r="4.5" fill="${p.accent}"/>
  <text x="80" y="40" font-family="'Manrope', sans-serif" font-weight="800"
    font-size="28" letter-spacing="1.5" fill="${p.text}">CAMPUSCO</text>
  <rect x="80" y="48" width="18" height="2" fill="${p.accent}"/>
  <text x="104" y="58" font-family="'Manrope', sans-serif" font-weight="600"
    font-size="9" letter-spacing="3" fill="${p.sub}">PROPERTY MANAGEMENT</text>
</svg>`;
};
